// Keep this file import-free: it is bundled into every app's _app, and @sortlist-frontend/utils adds ~14 kB there.

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const NUMBER_PATTERN = /\d+/g;
const MAX_FINGERPRINT_LENGTH = 200;

export const messageFingerprint = (message: string): string =>
  (message.split('\n')[0] ?? '')
    .replace(UUID_PATTERN, '<uuid>')
    .replace(NUMBER_PATTERN, 'N')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_FINGERPRINT_LENGTH);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;

interface ScopeLike {
  update: (context: unknown) => ScopeLike;
  setFingerprint: (fingerprint: string[]) => unknown;
  getScopeData: () => { fingerprint?: string[] };
}

const fingerprintAfterMerging =
  (context: unknown, fingerprint: string[]) =>
  (scope: ScopeLike): ScopeLike => {
    scope.update(context);
    if ((scope.getScopeData().fingerprint?.length ?? 0) === 0) scope.setFingerprint(fingerprint);
    return scope;
  };

type StackStart = (...args: never[]) => unknown;

const errorFromCallSite = (message: string, stackStart: StackStart): Error => {
  const error = new Error(message);
  Error.captureStackTrace?.(error, stackStart);
  return error;
};

export const groupedCaptureArgs = (
  exception: unknown,
  context: unknown,
  stackStart: StackStart,
): [unknown, unknown] => {
  if (typeof exception !== 'string') return [exception, context];

  const error = errorFromCallSite(exception, stackStart);
  const fingerprint = [messageFingerprint(exception)];

  if (context == null || isPlainObject(context)) {
    return [error, { ...context, fingerprint: context?.fingerprint ?? fingerprint }];
  }
  return [error, fingerprintAfterMerging(context, fingerprint)];
};
